<ng-container *ngIf="managementData$ | async as managementData; else noData">
  <markant-spinner
    *ngIf="isLoading"
    type="circle"
    colorScheme="primary"
  ></markant-spinner>
  <div class="header">
    <markant-platform-ui-page-header class="headline">
      {{ managementData.cartridgeVariantName }}

      @if (
        managementData.bookingState !== 'UPGRADED' &&
        (managementData.serviceUpgradeState === 'UPGRADE_REQUEST_OPEN' ||
          managementData.serviceUpgradeState === 'UPGRADE_REQUEST_APPROVED')
      ) {
        <markant-chip
          [colorScheme]="
            bookingUpgradeStateMap[managementData.serviceUpgradeState].color
          "
          [label]="
            bookingUpgradeStateMap[managementData.serviceUpgradeState].label
              | transloco
          "
          [title]="
            bookingUpgradeStateMap[managementData.serviceUpgradeState].label
              | transloco
          "
          [readonly]="true"
        >
          <markant-icon
            slot="markant-chip-icon-leading"
            [icon]="
              bookingUpgradeStateMap[managementData.serviceUpgradeState].icon
            "
          ></markant-icon>
        </markant-chip>
      } @else {
        <markant-chip
          [colorScheme]="
            bookingStateMap[managementData.usageRightActivationState].color
          "
          [label]="
            'usageRight.activationState.' +
              managementData.usageRightActivationState | transloco
          "
          [title]="
            'usageRight.activationState.' +
              managementData.usageRightActivationState | transloco
          "
          [readonly]="true"
        >
          @if (
            !!bookingStateMap[managementData.usageRightActivationState].icon
          ) {
            <markant-icon
              slot="markant-chip-icon-leading"
              [icon]="
                bookingStateMap[managementData.usageRightActivationState].icon
              "
            ></markant-icon>
          }
        </markant-chip>
      }
      <div class="edit-toolbar" actions>
        @if (
          ((isPlatformAdmin$ | async) || (isServiceAdmin$ | async)) &&
          allowSuggestUpgrade
        ) {
          <div id="suggest-upgrade-button">
            <markant-button
              data-cy="management-view-suggest-upgrade-button"
              colorScheme="primary"
              variant="clear"
              (click)="toggleSuggestUpgradeDialogConfirmation()"
              [disabled]="disableSuggestUpgrade"
            >
              <markant-icon icon="m_sent" slot="icon-leading"></markant-icon>
              {{ 'cartridge.management.suggestUpgrade.label' | transloco }}
            </markant-button>
          </div>
          @if (suggestUpgradeTooltip) {
            <markant-tooltip
              class="suggest-upgrade-tooltip-position"
              for="suggest-upgrade-button"
              placement="top"
              trigger="mouseenter"
              [label]="suggestUpgradeTooltip"
            ></markant-tooltip>
          }
        }
        @if ((isPlatformAdmin$ | async) && isUpgradable) {
          <markant-button
            data-cy="management-view-upgrade-button"
            colorScheme="primary"
            variant="clear"
            (click)="openVariantUpgradeWizard(true)"
          >
            <markant-icon icon="m_plus" slot="icon-leading"></markant-icon>
            {{ 'cartridge.management.upgrade' | transloco }}
          </markant-button>
        }
        <markant-button
          *ngIf="
            ((isPlatformAdmin$ | async) || (isServiceAdmin$ | async)) &&
            canBlockService
          "
          colorScheme="primary"
          variant="clear"
          (click)="confirmServiceBlockDialogToggle(true)"
        >
          <markant-icon
            [icon]="serviceBlocked ? 'm_unlock-small' : 'm_lock-small'"
            slot="icon-leading"
          ></markant-icon>
          <markant-tooltip
            placement="top"
            [label]="
              serviceBlocked
                ? ('cartridge.management.block.unBlockTooltip' | transloco)
                : ('cartridge.management.block.blockTooltip' | transloco)
            "
          ></markant-tooltip>
          {{
            serviceBlocked
              ? ('cartridge.management.block.unBlock' | transloco)
              : ('cartridge.management.block.block' | transloco)
          }}
        </markant-button>
        @if (isPlatformAdmin$ | async) {
          <markant-button
            [disabled]="
              !usageRight ||
              usageRight.state === UsageRightState.STOPPED ||
              managementData.bookingState === 'UPGRADED'
            "
            colorScheme="primary"
            variant="clear"
            [class.disabled]="
              !usageRight ||
              usageRight.state === UsageRightState.STOPPED ||
              managementData.bookingState === 'UPGRADED'
            "
            (click)="isUsageRightEditModalOpen = true"
          >
            @if (
              usageRight?.state === UsageRightState.STOPPED ||
              managementData.bookingState === 'UPGRADED'
            ) {
              <markant-tooltip
                [label]="'cartridge.management.editNotPossible' | transloco"
              ></markant-tooltip>
            }
            <markant-icon icon="m_edit" slot="icon-leading"></markant-icon>
            {{ 'cartridge.management.edit' | transloco }}
          </markant-button>
        }
      </div>
    </markant-platform-ui-page-header>

    <h6 *ngIf="managementData.cancellationDate" class="cancellation-text">
      {{
        'cartridge.management.cancellation.managementView.cancelledAndActiveUntil'
          | transloco
      }}
      {{ managementData.cancellationDate | translocoDate: dateFormatOptions }}
    </h6>
  </div>

  <div class="management-section">
    <markant-platform-management-tile-contract-data
      [managementData]="managementData"
    >
    </markant-platform-management-tile-contract-data>

    <markant-platform-management-tile-service-responsibility
      *ngIf="isOrgQualificationLevel3 && managementData.serviceManagerId"
      [serviceManagerId]="managementData.serviceManagerId"
      [serviceManager]="managementData.serviceManager"
      [organizationId]="managementData.organizationId"
      [bookingState]="managementData.bookingState"
      [bookingId]="bookingId"
      [usageRightSuspended]="
        ['SUSPENDED'] | arrayIncludes: managementData.usageRightState
      "
      (serviceManagerUpdated)="onBookingUpdated()"
    >
    </markant-platform-management-tile-service-responsibility>

    <markant-platform-management-tile-payment-data
      *ngIf="managementData.price"
      [price]="managementData.price"
      [setUpCost]="managementData.setUpCost"
    >
    </markant-platform-management-tile-payment-data>

    <markant-platform-management-tile-bulk-price
      *ngIf="isBulkPrice"
      [price]="managementData.price"
      [servicePackage]="managementData.cartridgeServicePackages"
    >
    </markant-platform-management-tile-bulk-price>

    <markant-platform-management-tile-calculation-parameters
      *ngIf="
        managementData.calculationParameters &&
        managementData.calculationParameters.length > 0
      "
      [calculationParametersData]="managementData.calculationParameters"
    >
    </markant-platform-management-tile-calculation-parameters>

    <markant-platform-management-tile-service-documents
      [priceRequestId]="managementData.priceRequestId"
      [bookingState]="managementData.bookingState"
      [variantId]="managementData.cartridgeVariantId"
    >
    </markant-platform-management-tile-service-documents>

    <markant-platform-management-tile-eligible-user
      [bookingState]="managementData.bookingState"
      [variantId]="managementData.cartridgeVariantId"
      [usageRightState]="managementData.usageRightState"
    >
    </markant-platform-management-tile-eligible-user>

    <ng-container
      *ngIf="
        (['BOOKED', 'TO_BE_CANCELLED', 'ACCEPTED']
          | arrayIncludes: managementData.bookingState) &&
        !(['SUSPENDED'] | arrayIncludes: managementData.usageRightState)
      "
    >
      <markant-platform-management-tile-cancellation
        [serviceId]="managementData.id"
      >
      </markant-platform-management-tile-cancellation>
      <markant-platform-usage-report-tile
        *ngIf="isBulkPrice"
        [variantName]="managementData.cartridgeVariantName"
      >
      </markant-platform-usage-report-tile>
    </ng-container>
  </div>
</ng-container>

<ng-template #noData>
  <markant-platform-info-callout
    *ngIf="!isLoading"
    [translationKey]="'cartridge.management.noManagementData'"
  >
  </markant-platform-info-callout>
</ng-template>

<markant-platform-usage-right-edit-modal
  *ngIf="isUsageRightEditModalOpen && usageRight"
  [usageRight]="usageRight!"
  (dialogFinished)="onCloseUsageRightEditDialog($event)"
>
</markant-platform-usage-right-edit-modal>

@if (bookingData() && isOpenVariantUpgradeWizard) {
  <markant-platform-upgrade-variant-modal
    [organization]="organizationData"
    [booking]="bookingData()!"
    (closeDialog)="openVariantUpgradeWizard(false)"
    (bookingUpdated)="onBookingUpdated()"
  />
}
@if (isConfirmServiceBlockDialogOpen) {
  <markant-platform-modal-bridge>
    <markant-platform-approve-dialog
      [open]="true"
      class="mfa-approve-dialog"
      [headline]="
        serviceBlocked
          ? ('cartridge.management.block.unBlockConfirmationDialog.headline'
            | transloco)
          : ('cartridge.management.block.blockConfirmationDialog.headline'
            | transloco)
      "
      [text]="
        serviceBlocked
          ? ('cartridge.management.block.unBlockConfirmationDialog.text'
            | transloco)
          : ('cartridge.management.block.blockConfirmationDialog.text'
            | transloco)
      "
      [approveButtonText]="'common.confirm' | transloco"
      (isApproved)="blockConfirmation()"
      (openChange)="confirmServiceBlockDialogToggle(false)"
    >
    </markant-platform-approve-dialog>
  </markant-platform-modal-bridge>
}

@if (isSuggestUpgradeDialogOpen) {
  <markant-platform-modal-bridge>
    <ng-container
      *ngIf="managementData$ | async as managementData; else noData"
    >
      <markant-platform-approve-dialog
        [open]="true"
        class="mfa-approve-dialog"
        [headline]="
          'cartridge.management.suggestUpgrade.confirmationModal.headline'
            | transloco
        "
        [text]="
          'cartridge.management.suggestUpgrade.confirmationModal.text'
            | transloco: { serviceManager: managementData.serviceManager }
        "
        [approveButtonText]="
          'cartridge.management.suggestUpgrade.confirmationModal.confirmButton'
            | transloco
        "
        (isApproved)="confirmSuggestUpgrade()"
        (openChange)="toggleSuggestUpgradeDialogConfirmation()"
      >
      </markant-platform-approve-dialog>
    </ng-container>
  </markant-platform-modal-bridge>
}
